import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, RefreshCw, Upload, Package, CheckCircle, Clock, XCircle, Search, Bell, Trash2, Eye, Plus, User as UserIcon, Settings, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import DragDropUpload from "@/components/DragDropUpload";
import CreateOrderDialog from "@/components/CreateOrderDialog";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import { getStatusInfo, shouldShowPingButton } from "@/utils/orderHelpers";
import { statusBadgeStyle } from "@/lib/statusColors";
import { useBranding } from "@/contexts/BrandingContext";
import { getStageLabel, getStatusLabel, getActiveStages, getActiveStatuses } from "@/utils/labelMapper";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// How many times to silently retry before showing an error toast
const MAX_SILENT_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { workflowConfig } = useBranding();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewProofsDialogOpen, setViewProofsDialogOpen] = useState(false);
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderObj, setSelectedOrderObj] = useState(null);
  const [uploadStage, setUploadStage] = useState("clay");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [viewProofsStage, setViewProofsStage] = useState("clay");

  // ── FIX: authReady gate prevents fetchOrders racing the axios header write ──
  // On a fresh login the token is written to localStorage and then this component
  // mounts.  Previously, axios.defaults.headers was set and fetchOrders() were
  // called in the *same* useEffect tick.  Axios could fire the request before the
  // header mutation was picked up, producing a 401 → "Failed to load orders" toast
  // even though the user just logged in successfully.
  //
  // Solution: split into two effects.
  //   Effect 1 – runs once on mount: validates the token, writes the header, then
  //              flips authReady → true.
  //   Effect 2 – runs only when authReady becomes true: kicks off data fetches.
  // This guarantees the header is committed before any API call goes out.
  const [authReady, setAuthReady] = useState(false);
  const retryCountRef = useRef(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage] = useState(50);

  // ── Effect 1: auth setup only ────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    // Write the header synchronously, then signal ready.
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAuthReady(true);
  }, [navigate]);

  // ── Effect 2: data fetches — only after auth header is guaranteed set ────────
  useEffect(() => {
    if (!authReady) return;
    checkDashboardPreference();
    fetchOrders();
    fetchVendors();
  }, [authReady]);

  const checkDashboardPreference = async () => {
    try {
      const response = await axios.get(`${API}/settings/tenant`);
      const defaultDashboard = response.data.settings?.default_dashboard;
      if (defaultDashboard === 'orderdesk') {
        navigate('/admin/orderdesk');
      }
    } catch (error) {
      console.error("Failed to check dashboard preference:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors/list`);
      setVendors(response.data.vendors);
    } catch (error) {
      console.error("Failed to load vendors", error);
    }
  };

  useEffect(() => {
    // Filter and sort orders based on all criteria
    let filtered = orders;

    // Apply archive filter
    filtered = filtered.filter(order =>
      showArchived
        ? (order.is_archived || order.stage === "archived")
        : (!order.is_archived && order.stage !== "archived")
    );

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number.toLowerCase().includes(query) ||
        (order.customer_email && order.customer_email.toLowerCase().includes(query)) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(query))
      );
    }

    // Apply stage filter
    if (stageFilter !== "all") {
      filtered = filtered.filter(order => order.stage === stageFilter);
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(order =>
        order.clay_status === statusFilter || order.paint_status === statusFilter
      );
    }

    // Apply vendor filter
    if (vendorFilter !== "all") {
      filtered = filtered.filter(order => order.item_vendor === vendorFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "created_at") {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === "updated_at") {
        return new Date(b.updated_at) - new Date(a.updated_at);
      } else if (sortBy === "last_edited_by_customer") {
        const aTime = a.clay_approval?.created_at || a.paint_approval?.created_at || a.created_at;
        const bTime = b.clay_approval?.created_at || b.paint_approval?.created_at || b.created_at;
        return new Date(bTime) - new Date(aTime);
      }
      return 0;
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedOrders = filtered.slice(startIndex, endIndex);

    setFilteredOrders(paginatedOrders);
    setTotalPages(Math.ceil(filtered.length / perPage));
    setTotalCount(filtered.length);
  }, [orders, searchQuery, stageFilter, statusFilter, vendorFilter, showArchived, currentPage, perPage, sortBy]);

  // ── fetchOrders: silent retry before showing the error toast ─────────────────
  // Previously the toast fired immediately on the first failure.  On a slow
  // connection or a cold backend the first request can time out even with a valid
  // token, causing a scary red toast right after login.  Now we retry up to
  // MAX_SILENT_RETRIES times with a short delay before surfacing the error.
  const fetchOrders = async (page = 1, attempt = 0) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders?limit=200`, {
        timeout: 30000,
      });
      const data = response.data;

      // Handle both paginated and legacy array responses
      let ordersData;
      if (data.orders) {
        ordersData = data.orders;
      } else {
        ordersData = data;
      }

      const sortedOrders = ordersData.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );

      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedOrders = sortedOrders.slice(startIndex, endIndex);

      setOrders(sortedOrders);
      setFilteredOrders(paginatedOrders);
      setCurrentPage(page);
      setTotalPages(Math.ceil(sortedOrders.length / perPage));
      setTotalCount(sortedOrders.length);

      // Reset retry counter on success
      retryCountRef.current = 0;
    } catch (error) {
      console.error(`fetchOrders attempt ${attempt + 1} failed:`, error);

      if (attempt < MAX_SILENT_RETRIES) {
        // Retry silently — do NOT show a toast yet
        retryCountRef.current = attempt + 1;
        setTimeout(() => fetchOrders(page, attempt + 1), RETRY_DELAY_MS);
        // Keep loading spinner on while retrying
        return;
      }

      // All retries exhausted — now show the toast
      toast.error("Failed to load orders. Please refresh the page.");
    } finally {
      // Only clear the spinner on success or final failure, not during retries
      if (attempt >= MAX_SILENT_RETRIES || !loading) {
        setLoading(false);
      }
    }
  };

  const syncOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/sync-orders`);
      toast.success(response.data.message);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to sync orders. Please check Shopify credentials.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProofs = async () => {
    if (!selectedOrder || uploadFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    const formData = new FormData();
    formData.append("stage", uploadStage);

    for (let file of uploadFiles) {
      formData.append("files", file);
    }

    try {
      await axios.post(`${API}/admin/orders/${selectedOrder}/proofs`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Proofs uploaded! Status updated to 'Feedback Needed'");
      setUploadDialogOpen(false);
      setUploadFiles([]);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to upload proofs");
      console.error(error);
    }
  };

  const handleDeleteProof = async (orderId, proofId, stage) => {
    if (!window.confirm("Are you sure you want to delete this proof image?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/orders/${orderId}/proofs/${proofId}?stage=${stage}`);
      toast.success("Proof deleted successfully");
      fetchOrders();

      if (selectedOrderObj && selectedOrderObj.id === orderId) {
        const updatedOrder = orders.find(o => o.id === orderId);
        if (updatedOrder) {
          setSelectedOrderObj(updatedOrder);
        }
      }
    } catch (error) {
      toast.error("Failed to delete proof");
      console.error(error);
    }
  };

  const handleArchiveOrder = async (orderId, archive = true) => {
    const action = archive ? "archive" : "unarchive";
    if (!window.confirm(`Are you sure you want to ${action} this order?`)) {
      return;
    }

    try {
      await axios.patch(`${API}/admin/orders/${orderId}/archive?archive=${archive}`);
      toast.success(`Order ${action}d successfully`);
      fetchOrders();
    } catch (error) {
      toast.error(`Failed to ${action} order`);
      console.error(error);
    }
  };

  const handlePingCustomer = async (orderId, orderNumber, stage) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/ping-customer?stage=${stage}`);
      toast.success(`Reminder sent to customer for Order #${orderNumber}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
      console.error(error);
    }
  };

  const handleStatusChange = async (orderId, orderNumber, field, value) => {
    try {
      const params = { [field]: value };
      await axios.patch(`${API}/admin/orders/${orderId}/update-status`, null, { params });
      toast.success(`${field} updated to: ${value}`);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  const openUploadDialog = (order, stage) => {
    setSelectedOrder(order.id);
    setSelectedOrderObj(order);
    setUploadStage(stage);
    setUploadDialogOpen(true);
  };

  const formatTimestamp = (timestamp, updatedBy) => {
    if (!timestamp) return "No updates yet";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let timeAgo;
    if (diffMins < 1) timeAgo = "just now";
    else if (diffMins < 60) timeAgo = `${diffMins}m ago`;
    else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
    else timeAgo = `${diffDays}d ago`;

    const by = updatedBy === "customer" ? "by Customer" : "by Admin";
    return `${timeAgo} ${by}`;
  };

  const formatStageTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case "clay": return "bg-yellow-500";
      case "paint": return "bg-blue-500";
      case "shipped": return "bg-green-500";
      default: return "bg-gray-500";
    }
  };

  const getApprovalIcon = (order, stage) => {
    const statusField = `${stage}_status`;
    const status = order[statusField];

    if (status === "sculpting") return <Clock className="w-4 h-4 text-gray-400" />;
    if (status === "feedback_needed") return <Bell className="w-4 h-4 text-blue-500" />;
    if (status === "approved") return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === "changes_requested") return <XCircle className="w-4 h-4 text-orange-500" />;
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const getStatusBadge = (status) => {
    const info = getStatusInfo(status);
    return (
      <Badge className="text-xs font-bold capitalize border-0" style={statusBadgeStyle(status, { glow: true })}>
        {info.adminLabel}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-4xl font-extrabold bg-gradient-to-r from-fuchsia-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/admin/users')}
              variant="outline"
              className="border-purple-200 text-purple-600 hover:bg-purple-50"
            >
              <UserIcon className="w-4 h-4 mr-2" />
              Users
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              onClick={syncOrders}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="sync-orders-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Sync
            </Button>
            <Button
              onClick={() => {
                localStorage.removeItem('admin_token');
                navigate('/admin/login');
                toast.success('Logged out successfully');
              }}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <AnalyticsDashboard />

        {/* Sticky Search and Filter Bar */}
        <div className="sticky top-0 z-10 bg-white shadow-md border-b mb-6 py-4 -mx-8 px-8">
          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10"
                data-testid="search-input"
              />
            </div>
            <div className="w-48">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Order Date (Newest)</SelectItem>
                  <SelectItem value="updated_at">Last Updated</SelectItem>
                  <SelectItem value="last_edited_by_customer">Customer Activity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger data-testid="stage-filter" className="h-10">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="clay">{getStageLabel('clay', workflowConfig)}</SelectItem>
                  <SelectItem value="paint">{getStageLabel('paint', workflowConfig)}</SelectItem>
                  <SelectItem value="fulfilled">{getStageLabel('shipped', workflowConfig)}</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="h-10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="sculpting">{getStatusLabel('sculpting', workflowConfig)}</SelectItem>
                  <SelectItem value="feedback_needed">{getStatusLabel('feedback_needed', workflowConfig)}</SelectItem>
                  <SelectItem value="approved">{getStatusLabel('approved', workflowConfig)}</SelectItem>
                  <SelectItem value="changes_requested">{getStatusLabel('changes_requested', workflowConfig)}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-48">
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger data-testid="vendor-filter" className="h-10">
                  <SelectValue placeholder="Vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.name} value={vendor.name}>
                      {vendor.name} ({vendor.order_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => setCreateOrderDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700 h-10 whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Order
            </Button>
          </div>
        </div>

        <div className="grid gap-6">
          {filteredOrders.length === 0 && !loading && !searchQuery && (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-4">No orders found</p>
                <Button onClick={syncOrders} data-testid="sync-first-orders-btn">
                  Sync Orders from Shopify
                </Button>
              </CardContent>
            </Card>
          )}

          {filteredOrders.length === 0 && !loading && searchQuery && (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-4">No orders found matching "{searchQuery}"</p>
                <Button variant="outline" onClick={() => setSearchQuery("")}>
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          )}

          {filteredOrders.map((order) => {
            const isFulfilled = order.shopify_fulfillment_status === 'fulfilled' || order.stage === 'fulfilled';
            return (
              <Card
                key={order.id}
                className={`hover:shadow-lg transition-shadow cursor-pointer ${isFulfilled ? 'bg-gray-50 opacity-70' : ''}`}
                data-testid={`order-card-${order.id}`}
                onClick={() => {
                  sessionStorage.setItem('orderDetailsReturnPath', '/admin/dashboard');
                  navigate(`/admin/orders/${order.id}`);
                }}
              >
                <CardContent className="p-4">
                  <div className="grid grid-cols-[240px_1fr] gap-4">
                    {/* Left side - Order Info */}
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <h3 className="text-lg font-bold">Order #{order.order_number}</h3>
                          {order.is_manual_order && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs py-0">
                              Manual
                            </Badge>
                          )}
                          {isFulfilled && (
                            <Badge className="bg-green-500 text-white text-xs py-0">
                              Fulfilled
                            </Badge>
                          )}
                          {order.parent_order_id && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs py-0">
                              Sub-Order
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-700 truncate">{order.customer_name}</p>
                        <p className="text-xs text-gray-600 truncate">{order.customer_email}</p>
                        {order.item_vendor && (
                          <p className="text-xs text-purple-600 font-semibold">
                            Vendor: {order.item_vendor}
                          </p>
                        )}
                        {order.tracking_number && (
                          <p className="text-xs text-blue-600 font-semibold mt-1">
                            📦 Tracking: <a
                              href={order.tracking_url || `https://www.google.com/search?q=track+${order.tracking_number}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-blue-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {order.tracking_number}
                            </a>
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Last updated: {formatTimestamp(order.last_updated_at, order.last_updated_by)}
                        </p>
                      </div>

                      {/* Admin Controls */}
                      <div className="space-y-1.5">
                        <Select
                          value={order.stage}
                          onValueChange={(value) => handleStatusChange(order.id, order.order_number, 'stage', value)}
                        >
                          <SelectTrigger className={`h-8 text-xs font-bold uppercase text-white border-0 ${
                            order.stage === 'clay' ? 'bg-yellow-500' :
                            order.stage === 'paint' ? 'bg-blue-500' :
                            order.stage === 'fulfilled' ? 'bg-green-500' :
                            order.stage === 'canceled' ? 'bg-red-500' : 'bg-gray-500'
                          }`} data-testid={`stage-control-${order.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="clay">Stage: {getStageLabel('clay', workflowConfig)}</SelectItem>
                            <SelectItem value="paint">Stage: {getStageLabel('paint', workflowConfig)}</SelectItem>
                            <SelectItem value="fulfilled">Stage: {getStageLabel('shipped', workflowConfig)}</SelectItem>
                            <SelectItem value="canceled">Stage: Canceled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-7 text-xs"
                          onClick={() => navigate(`/order/${order.id}`, { state: { order, isAdmin: true } })}
                          data-testid={`view-details-btn-${order.id}`}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                          onClick={() => handleArchiveOrder(order.id, !order.is_archived)}
                        >
                          {order.is_archived ? "Unarchive" : "Archive"}
                        </Button>
                      </div>
                    </div>

                    {/* Right side - Stages (Horizontal) */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {/* Clay Stage */}
                      <div className="relative p-3 bg-yellow-50 rounded-lg border border-yellow-200 min-w-0">
                        <Badge className="absolute top-2 right-2 text-xs py-0 uppercase font-bold border-0" style={statusBadgeStyle(order.clay_status, { glow: true })}>{getStatusLabel(order.clay_status, workflowConfig)}</Badge>
                        <h4 className="font-bold text-sm mb-1 pr-12">{getStageLabel('clay', workflowConfig)}</h4>
                        {order.clay_entered_at && (
                          <p className="text-xs text-gray-500 mb-2">
                            {formatStageTimestamp(order.clay_entered_at)}
                          </p>
                        )}

                        <div className="space-y-2">
                          <Select
                            value={order.clay_status}
                            onValueChange={(value) => handleStatusChange(order.id, order.order_number, 'clay_status', value)}
                          >
                            <SelectTrigger className="h-7 text-xs w-full" data-testid={`clay-status-control-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sculpting">{getStatusLabel('sculpting', workflowConfig)}</SelectItem>
                              <SelectItem value="feedback_needed">{getStatusLabel('feedback_needed', workflowConfig)}</SelectItem>
                              <SelectItem value="approved">{getStatusLabel('approved', workflowConfig)}</SelectItem>
                              <SelectItem value="changes_requested">{getStatusLabel('changes_requested', workflowConfig)}</SelectItem>
                            </SelectContent>
                          </Select>

                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            {getApprovalIcon(order, "clay")}
                            <span className="truncate">{order.clay_proofs?.length || 0} proofs</span>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              className="flex-1 min-w-[70px] h-7 text-xs"
                              onClick={() => openUploadDialog(order, "clay")}
                              data-testid={`upload-clay-btn-${order.id}`}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Upload
                            </Button>
                            {order.clay_proofs?.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-purple-500 text-purple-600 hover:bg-purple-50"
                                onClick={() => {
                                  setSelectedOrderObj(order);
                                  setViewProofsStage("clay");
                                  setViewProofsDialogOpen(true);
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 w-7 p-0 border-blue-500 text-blue-600 hover:bg-blue-50 shrink-0"
                              onClick={() => handlePingCustomer(order.id, order.order_number, "clay")}
                              data-testid={`ping-clay-btn-${order.id}`}
                            >
                              <Bell className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Paint Stage */}
                      <div className="relative p-3 bg-blue-50 rounded-lg border border-blue-200 min-w-0">
                        <Badge className="absolute top-2 right-2 text-xs py-0 uppercase font-bold border-0" style={statusBadgeStyle(order.paint_status, { glow: true })}>{getStatusLabel(order.paint_status, workflowConfig)}</Badge>
                        <h4 className="font-bold text-sm mb-1 pr-12">{getStageLabel('paint', workflowConfig)}</h4>
                        {order.paint_entered_at && (
                          <p className="text-xs text-gray-500 mb-2">
                            {formatStageTimestamp(order.paint_entered_at)}
                          </p>
                        )}

                        <div className="space-y-2">
                          <Select
                            value={order.paint_status}
                            onValueChange={(value) => handleStatusChange(order.id, order.order_number, 'paint_status', value)}
                          >
                            <SelectTrigger className="h-7 text-xs w-full" data-testid={`paint-status-control-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">{getStatusLabel('pending', workflowConfig)}</SelectItem>
                              <SelectItem value="sculpting">{getStatusLabel('sculpting', workflowConfig)}</SelectItem>
                              <SelectItem value="feedback_needed">{getStatusLabel('feedback_needed', workflowConfig)}</SelectItem>
                              <SelectItem value="approved">{getStatusLabel('approved', workflowConfig)}</SelectItem>
                              <SelectItem value="changes_requested">{getStatusLabel('changes_requested', workflowConfig)}</SelectItem>
                            </SelectContent>
                          </Select>

                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            {getApprovalIcon(order, "paint")}
                            <span className="truncate">{order.paint_proofs?.length || 0} proofs</span>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              className="flex-1 min-w-[70px] h-7 text-xs"
                              onClick={() => openUploadDialog(order, "paint")}
                              data-testid={`upload-paint-btn-${order.id}`}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Upload
                            </Button>
                            {order.paint_proofs?.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-purple-500 text-purple-600 hover:bg-purple-50"
                                onClick={() => {
                                  setSelectedOrderObj(order);
                                  setViewProofsStage("paint");
                                  setViewProofsDialogOpen(true);
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 w-7 p-0 border-blue-500 text-blue-600 hover:bg-blue-50 shrink-0"
                              onClick={() => handlePingCustomer(order.id, order.order_number, "paint")}
                              data-testid={`ping-paint-btn-${order.id}`}
                            >
                              <Bell className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between bg-white p-4 rounded-lg border shadow-sm">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} orders
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <div className="flex items-center gap-2 px-4">
                <span className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Create Order Dialog */}
        <CreateOrderDialog
          open={createOrderDialogOpen}
          onOpenChange={setCreateOrderDialogOpen}
          onOrderCreated={fetchOrders}
        />

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="upload-dialog">
            <DialogHeader>
              <DialogTitle>Upload Proofs - {uploadStage.charAt(0).toUpperCase() + uploadStage.slice(1)} Stage</DialogTitle>
              <DialogDescription>
                Upload proof images for Order #{selectedOrderObj?.order_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Files (Drag & Drop or Click)</Label>
                <DragDropUpload
                  onFilesSelected={setUploadFiles}
                  accept="image/*,.zip"
                  multiple={true}
                />
              </div>
              <Button onClick={handleUploadProofs} className="w-full" data-testid="upload-submit-btn" disabled={uploadFiles.length === 0}>
                Upload {uploadFiles.length > 0 && `(${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''})`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View & Manage Proofs Dialog */}
        <Dialog open={viewProofsDialogOpen} onOpenChange={setViewProofsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Manage Proofs - {viewProofsStage.charAt(0).toUpperCase() + viewProofsStage.slice(1)} Stage
              </DialogTitle>
              <DialogDescription>
                Order #{selectedOrderObj?.order_number} - {selectedOrderObj?.customer_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedOrderObj && selectedOrderObj[`${viewProofsStage}_proofs`]?.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {selectedOrderObj[`${viewProofsStage}_proofs`].map((proof, idx) => (
                    <div key={proof.id || idx} className="relative group border rounded-lg overflow-hidden bg-gray-50">
                      <img
                        loading="lazy"
                        src={proof.url}
                        alt={proof.filename || `Proof ${idx + 1}`}
                        className="w-full h-64 object-contain"
                      />
                      <div className="p-2 bg-white border-t">
                        <p className="text-sm text-gray-600 truncate">{proof.filename}</p>
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-xs text-gray-500">
                            {new Date(proof.uploaded_at).toLocaleDateString()}
                          </p>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={() => handleDeleteProof(selectedOrderObj.id, proof.id, viewProofsStage)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No proofs uploaded for this stage yet.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;